// Shared by the game and its same-origin character picker. No gameplay bindings.
const nativeControls = 'button, a[href], input:not([type="hidden"]), select, textarea';
const directions = {left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1]};

export function nearestItem(origin, items, direction) {
    const [dx, dy] = directions[direction];
    const x = (origin.left + origin.right) / 2, y = (origin.top + origin.bottom) / 2;
    let best = null, bestScore = Infinity;
    for (const item of items) {
        const rect = item.rect;
        const cx = (rect.left + rect.right) / 2, cy = (rect.top + rect.bottom) / 2;
        const forward = (cx - x) * dx + (cy - y) * dy;
        if (forward <= 2) continue;
        const cross = Math.abs(dx ? cy - y : cx - x);
        const inLine = dx ? rect.bottom > origin.top + 2 && rect.top < origin.bottom - 2
            : rect.right > origin.left + 2 && rect.left < origin.right - 2;
        const score = (inLine ? 0 : 100000) + forward + cross * 2;
        if (score < bestScore) { best = item; bestScore = score; }
    }
    return best?.element ?? null;
}

export class MenuGamepad {
    constructor() { this.reset(); }
    reset() { this.previous = new Set(); this.direction = null; this.repeatAt = 0; this.ready = false; this.id = null; }
    read(pad, now) {
        const pressed = index => !!pad?.buttons[index]?.pressed || pad?.buttons[index]?.value > 0.5;
        const held = new Set();
        if (pressed(0)) held.add('confirm');
        if (pressed(1)) held.add('back');
        if (pressed(9)) held.add('pause');
        const x = pad?.axes[0] || 0, y = pad?.axes[1] || 0;
        let direction = pressed(12) ? 'up' : pressed(13) ? 'down' : pressed(14) ? 'left' : pressed(15) ? 'right' : null;
        // Hysteresis keeps a stick near the threshold from repeatedly navigating.
        const threshold = this.direction ? 0.35 : 0.55;
        if (!direction && Math.max(Math.abs(x), Math.abs(y)) > threshold) {
            direction = Math.abs(x) > Math.abs(y) ? (x < 0 ? 'left' : 'right') : (y < 0 ? 'up' : 'down');
        }
        const id = pad ? `${pad.index}:${pad.id}` : null;
        if (id !== this.id) { this.reset(); this.id = id; }
        if (!this.ready) {
            this.ready = !held.size && !direction;
            this.previous = held;
            return [];
        }
        const commands = [...held].filter(command => !this.previous.has(command));
        if (direction && (direction !== this.direction || now >= this.repeatAt)) {
            commands.push(direction);
            this.repeatAt = now + (direction === this.direction ? 110 : 350);
        }
        this.direction = direction;
        this.previous = held;
        return commands;
    }
}

export function isVisible(element, ignoreOpacity = false) {
    if (!element?.isConnected || element.closest('[hidden], [inert]') || !element.getClientRects().length) return false;
    if (element.checkVisibility) return element.checkVisibility({checkOpacity: !ignoreOpacity, checkVisibilityCSS: true});
    const style = element.ownerDocument.defaultView.getComputedStyle(element);
    return style.visibility !== 'hidden' && style.display !== 'none' && (ignoreOpacity || style.opacity !== '0');
}

export class MenuNavigation {
    constructor({document: doc = document, enabled = () => true, onChange = () => {}, onExit = () => {}, labels = {cancel: 'Cancel', chooseOption: 'Choose an option'}} = {}) {
        this.document = doc;
        this.window = doc.defaultView;
        this.enabled = enabled;
        this.onChange = onChange;
        this.onExit = onExit;
        this.labels = labels;
        this.scopes = [];
        this.active = null;
        this.serial = 0;
        this.mode = 'mouse';
        this.dirty = true;
        this.items = [];
        this.pad = new MenuGamepad();
        this.capturePad = new MenuGamepad();
        this.swallowed = new Set();
        this.wasCapturing = false;
        this.rootsDirty = true;
        this.observer = new this.window.MutationObserver(records => this.noteMutations(records));
        this.observer.observe(doc.documentElement, {subtree: true, childList: true, attributes: true,
            attributeFilter: ['style', 'class', 'hidden', 'disabled', 'aria-disabled']});
        this.window.addEventListener('keydown', event => this.keydown(event), true);
        this.window.addEventListener('keyup', event => {
            const swallowed = this.swallowed.delete(event.code || event.key);
            if (this.capturing) return;
            if (swallowed) { event.preventDefault(); event.stopImmediatePropagation(); }
        }, true);
        this.window.addEventListener('blur', () => { this.pad.reset(); this.swallowed.clear(); });
        this.window.addEventListener('resize', () => {
            this.dirty = true;
            this.window.requestAnimationFrame(() => {
                this.refresh();
                if (this.mode !== 'mouse') this.focus(this.initial());
            });
        });
        doc.addEventListener('pointermove', event => { if (event.movementX || event.movementY) this.setMode('mouse'); }, {passive: true});
        doc.addEventListener('pointerdown', event => {
            this.setMode('mouse'); this.refresh();
            const target = this.items.find(element => element === event.target || element.contains(event.target));
            if (target) this.remember(target);
        }, true);
        doc.addEventListener('focusin', event => {
            if (this.items.includes(event.target)) this.remember(event.target);
        });
        this.register('select-options', '.menu-select-popup', {selector: 'button', priority: 1000,
            defaultFocus: '[aria-selected="true"]', back: () => this.closeSelect()});
    }
    register(id, selector, options = {}) {
        this.scopes.push({id, selector, options, visible: false, order: 0, selected: null, index: 0, key: null});
        this.rootsDirty = true;
    }
    // Scope selectors only use classes and structure, so their roots only
    // change with elements or classes.
    noteMutations(records) {
        if (!records.length) return;
        this.dirty = true;
        if (this.rootsDirty) return;
        const isElement = node => node.nodeType === 1;
        this.rootsDirty = records.some(record => record.type === 'attributes'
            ? record.attributeName === 'class'
            : [...record.addedNodes, ...record.removedNodes].some(isElement));
    }
    get capturing() { return !!this.active?.root.querySelector('[data-nav-capture="true"]') || this.active?.root.dataset.navCapture === 'true'; }
    get child() {
        try { return this.active?.root.querySelector('iframe[data-menu-frame]')?.contentWindow.jollyMenuNavigation; }
        catch { return null; }
    }
    setMode(mode) {
        if (this.mode === mode) return;
        this.mode = mode;
        this.document.documentElement.dataset.menuInput = mode;
        if (mode === 'mouse') this.items.forEach(element => element.classList.remove('menu-selected'));
    }
    refresh() {
        let next = null;
        const enabled = this.enabled();
        this.noteMutations(this.observer.takeRecords());
        const findRoots = this.rootsDirty;
        this.rootsDirty = false;
        for (const scope of this.scopes) {
            if (findRoots) scope.root = this.document.querySelector(scope.selector);
            const visible = enabled && isVisible(scope.root) && (!scope.options.when || scope.options.when(scope.root));
            if (visible && !scope.visible) scope.order = ++this.serial;
            scope.visible = visible;
            if (visible && (!next || (scope.options.priority || 0) > (next.options.priority || 0) ||
                ((scope.options.priority || 0) === (next.options.priority || 0) && scope.order > next.order))) next = scope;
        }
        const changed = next !== this.active;
        if (changed) {
            this.items.forEach(element => { element.tabIndex = -1; element.classList.remove('menu-selected'); });
            this.active = next;
            this.pad.reset();
            this.dirty = true;
            this.onChange(next);
            if (!next) {
                this.items = [];
                if (this.mode !== 'mouse') this.onExit();
                return;
            }
        }
        if (!next) return;
        if (this.dirty) {
            this.items = this.collect(next);
            this.dirty = false;
            if (this.mode !== 'mouse') {
                const preferred = this.items.find(element => element.matches(next.options.defaultFocus || ':not(*)'));
                let target = this.items.includes(next.selected) ? next.selected :
                    this.items.find(element => next.key && element.dataset.navKey === next.key);
                if (next.provisional && preferred) { target = preferred; next.provisional = false; }
                if (!target) target = preferred;
                if (changed && !target) next.provisional = true;
                if (!target) target = this.items[Math.min(next.index, this.items.length - 1)];
                if (target && (changed || target !== next.selected || !this.items.includes(next.selected))) this.focus(target);
            }
        }
    }
    collect(scope) {
        const candidates = [...scope.root.querySelectorAll(`${nativeControls}${scope.options.selector ? ', ' + scope.options.selector : ''}`)];
        const unique = [...new Set(candidates)].filter(element => {
            const parentCandidate = element.parentElement?.closest('[data-nav-group-control]');
            if (parentCandidate && parentCandidate !== element) { element.tabIndex = -1; return false; }
            const excluded = scope.options.exclude && element.matches(scope.options.exclude);
            // Level cards fade in only after intersection. Include unloaded cards
            // so navigation can scroll to the next row and trigger their loading.
            if (excluded || element.disabled || element.getAttribute('aria-disabled') === 'true' || !isVisible(element, element.classList.contains('game')) ||
                element.closest('.game-template, .entry-template, .video-template, [data-nav-disabled="true"]')) {
                element.tabIndex = -1;
                return false;
            }
            return true;
        });
        for (const element of unique) {
            element.dataset.menuItem = '';
            element.tabIndex = element === scope.selected ? 0 : -1;
            if (!element.matches(nativeControls) && !element.hasAttribute('role')) element.setAttribute('role', 'button');
            scope.options.decorate?.(element);
        }
        return unique;
    }
    remember(element) {
        if (!this.active) return;
        this.active.selected = element;
        this.active.key = element.dataset.navKey || null;
        this.active.index = this.items.indexOf(element);
    }
    focus(element) {
        if (!this.items.includes(element)) return;
        this.items.forEach(item => { item.classList.toggle('menu-selected', item === element); item.tabIndex = item === element ? 0 : -1; });
        this.remember(element);
        element.focus({preventScroll: true});
        element.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'instant'});
    }
    initial() {
        return this.items.includes(this.active?.selected) ? this.active.selected :
            this.items.find(element => element.matches(this.active?.options.defaultFocus || ':not(*)')) || this.items[0];
    }
    command(command, source = 'keyboard') {
        this.refresh();
        if (!this.active) return false;
        this.active.provisional = false;
        this.setMode(source);
        if (this.child) {
            this.child.setMode(source);
            this.child.command(command, source);
            return true;
        }
        if (command === 'back') {
            const focused = this.document.activeElement;
            if (focused.matches('input:not([readonly]):not([type=checkbox]):not([type=radio]), textarea, [contenteditable=true]')) focused.blur();
            else this.active.options.back?.(this.active.root);
            this.refresh(); return true;
        }
        if (command === 'pause') { this.active.options.pause?.(this.active.root); this.refresh(); return true; }
        const current = this.initial();
        if (!current) return true;
        if (!this.items.includes(this.document.activeElement) && !this.active.selected) {
            this.focus(command === 'previous' ? this.items.at(-1) : current);
            if (command !== 'confirm') return true;
        }
        if (directions[command]) {
            const target = nearestItem(current.getBoundingClientRect(), this.items.filter(element => element !== current)
                .map(element => ({element, rect: element.getBoundingClientRect()})), command);
            this.focus(target || current);
        } else if (command === 'next' || command === 'previous') {
            const index = this.items.indexOf(current), step = command === 'next' ? 1 : -1;
            this.focus(this.items[(index + step + this.items.length) % this.items.length]);
        } else if (command === 'confirm') {
            this.focus(current);
            if (current.tagName === 'SELECT') this.openSelect(current);
            else if (!current.matches('input:not([type=checkbox]):not([type=radio]):not([type=button]), textarea')) current.click();
            this.refresh();
        }
        return true;
    }
    keydown(event) {
        // Menu shortcuts consume the event before document-level game input.
        if (event.isTrusted) this.onInteraction?.();
        this.refresh();
        if (this.capturing) return;
        const key = event.code || event.key;
        if (this.swallowed.has(key) && (!this.active || !directions[event.key.replace('Arrow', '').toLowerCase()])) {
            event.preventDefault(); event.stopImmediatePropagation(); return;
        }
        if (!this.active || event.isComposing) return;
        const target = event.target;
        const textField = target.matches?.('input:not([type=checkbox]):not([type=radio]):not([type=button]), textarea, [contenteditable=true]');
        if (textField && event.key !== 'Tab') {
            if (event.key === 'Escape') { target.blur(); this.setMode('keyboard'); event.preventDefault(); event.stopImmediatePropagation(); this.swallowed.add(key); }
            return;
        }
        if (target.tagName === 'SELECT' && event.key.startsWith('Arrow')) return;
        // A scope can preserve its own shortcuts while it owns keyboard focus.
        // Keep typing, key capture and held-key repeats out of these actions.
        if (!event.repeat && this.active.options.shortcut?.(event, this.active.root)) {
            event.preventDefault(); event.stopImmediatePropagation();
            this.swallowed.add(key);
            this.setMode('keyboard');
            this.refresh();
            return;
        }
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        const command = ({ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down', Enter: 'confirm', ' ': 'confirm', Escape: 'back', Tab: event.shiftKey ? 'previous' : 'next'})[event.key];
        if (!command) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.swallowed.add(key);
        this.command(command);
    }
    updateGamepad(pad, {enabled = true, now = performance.now()} = {}) {
        this.refresh();
        const capture = this.capturing;
        if (capture && enabled && this.active.root.dataset.navCaptureDevice === 'keyboard') {
            if (this.capturePad.read(pad, now).includes('back')) this.active.root.querySelector('.cancel')?.click();
        } else this.capturePad.reset();
        if (!enabled || !this.active || capture || this.wasCapturing) {
            this.pad.reset(); this.wasCapturing = capture; return;
        }
        for (const command of this.pad.read(pad, now)) {
            const previous = this.active;
            this.command(command, 'gamepad');
            if (this.active !== previous || this.capturing) break;
        }
    }
    openSelect(select) {
        this.closeSelect();
        const backdrop = this.document.createElement('div');
        backdrop.className = 'menu-select-backdrop';
        backdrop.onclick = () => this.closeSelect();
        this.document.body.appendChild(backdrop);
        const popup = this.document.createElement('div');
        popup.className = 'menu-select-popup';
        popup.setAttribute('role', 'dialog'); popup.setAttribute('aria-modal', 'true');
        popup.setAttribute('aria-label', select.getAttribute('aria-label') || select.labels?.[0]?.textContent || this.labels.chooseOption);
        for (const option of select.options) {
            const button = this.document.createElement('button');
            button.type = 'button'; button.textContent = option.textContent; button.disabled = option.disabled;
            button.setAttribute('aria-selected', String(option.selected));
            button.onclick = () => {
                const changed = select.value !== option.value;
                select.value = option.value;
                this.closeSelect();
                if (changed) { select.dispatchEvent(new this.window.Event('input', {bubbles: true})); select.dispatchEvent(new this.window.Event('change', {bubbles: true})); }
            };
            popup.appendChild(button);
        }
        const cancel = this.document.createElement('button'); cancel.type = 'button'; cancel.textContent = this.labels.cancel;
        cancel.onclick = () => this.closeSelect(); popup.appendChild(cancel);
        this.document.body.appendChild(popup);
        this.refresh();
    }
    closeSelect() {
        this.document.querySelector('.menu-select-popup')?.remove();
        this.document.querySelector('.menu-select-backdrop')?.remove();
        this.dirty = true;
    }
}
